import { Flex } from "@radix-ui/themes";
import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import styled from "@emotion/styled";
import {
  MemCommonEditor,
  MemCommonEditorEventHandler,
  MemCommonEditorInitializer,
  MemCommonEditorInstance,
  MemCommonEditorTheme,
  editorContextModule,
  MemCommonEditorActionKind,
  MemCommonEditorEventKind,
} from "@mem-labs/common-editor";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { INITIAL_NOTE_CONTENT_DOCUMENT_ENCODED_CONTENT } from "@/domains/tiptap/constants";
import { orderBy } from "lodash-es";
import { AlertTriangle } from "lucide-react";

export const NoteContentDocumentVersionInspector: FC<{ noteId: Uuid }> = ({ noteId }) => {
  const editorInstanceRef = useRef<MemCommonEditorInstance | null>(null);
  const previousEncodedContentRef = useRef<string>(INITIAL_NOTE_CONTENT_DOCUMENT_ENCODED_CONTENT);

  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);
  const [loadingSnapshotId, setLoadingSnapshotId] = useState<string | null>(null);
  const [loadedSnapshots, setLoadedSnapshots] = useState<string[]>([]);
  const [isOverwriteMode, setIsOverwriteMode] = useState(true);

  const snapshotsResult = useQuery({
    queryKey: ["note-content-document-snapshots", noteId],
    queryFn: () =>
      api.post("/internal/v1/note-content-document-snapshots/search", {
        body: {
          note_id: noteId,
          limit: 10000,
          offset: 0,
        },
      }),
  });

  // When a new snapshot is selected, mark it as loading
  useEffect(() => {
    if (selectedSnapshotId) {
      setLoadingSnapshotId(selectedSnapshotId);
    }
  }, [selectedSnapshotId]);

  const selectedSnapshotResult = useQuery({
    queryKey: ["note-content-document-snapshot", selectedSnapshotId],
    queryFn: async () => {
      if (!selectedSnapshotId) return null;

      const apiResponse = await api.get(
        "/internal/v1/note-content-document-snapshots/{note_content_document_snapshot_id}",
        {
          params: {
            path: {
              note_content_document_snapshot_id: selectedSnapshotId,
            },
          },
        }
      );

      const snapshotData = apiResponse.data;

      if (snapshotData) {
        setLoadedSnapshots(prev =>
          prev.includes(selectedSnapshotId) ? prev : [...prev, selectedSnapshotId]
        );
      }

      return apiResponse;
    },
    enabled: !!selectedSnapshotId,
    staleTime: Infinity,
  });

  useEffect(() => {
    const snapshotData = selectedSnapshotResult.data;

    if (snapshotData?.data?.encoded_content && editorInstanceRef.current) {
      if (isOverwriteMode) {
        editorInstanceRef.current.dispatchAction({
          kind: MemCommonEditorActionKind.OverwriteDocumentContent,
          payload: { encodedContent: snapshotData.data.encoded_content },
        });
      } else {
        editorInstanceRef.current.dispatchAction({
          kind: MemCommonEditorActionKind.ApplyRemoteUpdate,
          payload: { encodedContentUpdate: snapshotData.data.encoded_content },
        });
      }

      setLoadingSnapshotId(null);
    }
  }, [selectedSnapshotResult.data, selectedSnapshotId]);

  const editorInitializer: MemCommonEditorInitializer = useCallback(async () => {
    const defaultEditorContext = editorContextModule.generateDefaultEditorContext();
    return {
      encodedContent: "",
      toolbar: false,
      readOnly: true,
      autoFocus: false,
      noteId,
      enableWebSockets: false,
      maxUpdateThrottleMillis: 2000,
      theme: MemCommonEditorTheme.Light,
      metadata: undefined,
      webSocketAuthToken: undefined,
      context: defaultEditorContext,
    };
  }, [noteId]);

  const editorEventHandler: MemCommonEditorEventHandler = useCallback(event => {
    if (event.kind === MemCommonEditorEventKind.DocumentChanged) {
      previousEncodedContentRef.current = event.payload.encodedContentDiff;
    } else if (
      [MemCommonEditorEventKind.ContentError, MemCommonEditorEventKind.FatalError].includes(
        event.kind
      )
    ) {
      alert("Error: " + event.kind);
    }
  }, []);

  const handleReplaceNote = useCallback(async () => {
    if (!selectedSnapshotResult.data?.data?.encoded_content) return;
    const confirmMessage = `Are you sure? This will permanently replace the current note content with this version.`;
    const selectedNoteContentDocumentSnapshot = selectedSnapshotResult.data.data;
    if (window.confirm(confirmMessage)) {
      await api.post(
        "/internal/v1/note-content-documents/{note_content_document_id}/restore-from-snapshot-version",
        {
          params: {
            path: {
              note_content_document_id:
                selectedNoteContentDocumentSnapshot.note_content_document_id,
            },
          },
          body: {
            note_content_document_version:
              selectedNoteContentDocumentSnapshot.note_content_document_version,
          },
        }
      );
    }
  }, [noteId, selectedSnapshotResult.data]);

  const snapshots = snapshotsResult.data?.data?.results ?? [];
  const sortedSnapshots = orderBy(snapshots, x => x.note_content_document_version, "asc");
  const currentIndex = sortedSnapshots.findIndex(s => s.id === selectedSnapshotId);

  // Keyboard shortcuts for navigating snapshots
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "s" && currentIndex < sortedSnapshots.length - 1) {
        // Move down
        setSelectedSnapshotId(sortedSnapshots[currentIndex + 1].id);
      } else if (e.key === "w" && currentIndex > 0) {
        // Move up
        setSelectedSnapshotId(sortedSnapshots[currentIndex - 1].id);
      } else if (e.key === "u") {
        setIsOverwriteMode(false);
      } else if (e.key === "o") {
        setIsOverwriteMode(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, sortedSnapshots]);

  useEffect(() => {
    const el = document.getElementById(`snapshot-${selectedSnapshotId}`);
    if (el) {
      el.scrollIntoView({ behavior: "instant", block: "nearest" });
    }
  }, [selectedSnapshotId]);

  if (!snapshotsResult.data) {
    return <pre>Loading snapshots...</pre>;
  }

  if (snapshots.length === 0) {
    return <div>No snapshots found for this note.</div>;
  }

  return (
    <Flex direction="row" gap="4" style={{ alignItems: "flex-start", maxWidth: "100%" }}>
      {/* Sidebar with snapshots */}
      <Flex direction="column" gap="2" style={{ width: "250px", flexShrink: 0 }}>
        <p style={{ fontSize: "11px" }}>
          Press "s" to go down, "w" to go up.
          <br />
          {isOverwriteMode
            ? `In overwrite mode. Press "u" to switch to update mode.`
            : `In update mode. Press "o" to switch to overwrite mode.`}
        </p>
        <SnapshotList>
          {sortedSnapshots.map(snapshot => {
            const isSelected = snapshot.id === selectedSnapshotId;
            const isLoading = snapshot.id === loadingSnapshotId;
            const isApplied = loadedSnapshots.includes(snapshot.id);
            return (
              <SnapshotItem
                id={`snapshot-${snapshot.id}`}
                key={snapshot.id}
                selected={isSelected}
                onClick={() => setSelectedSnapshotId(snapshot.id)}
              >
                <Flex direction="row" gap="2" align="center">
                  <Flex direction="column" gap="1" style={{ fontSize: "11px" }}>
                    <div>
                      v{snapshot.note_content_document_version} -{" "}
                      {DateTime.fromISO(snapshot.note_content_document_locally_modified_at).year <
                      2024
                        ? "unknown (pre-snapshots)"
                        : DateTime.fromISO(
                            snapshot.note_content_document_locally_modified_at
                          ).toFormat("MMM d, yyyy h:mm a")}
                    </div>
                  </Flex>
                  <StatusIndicator>
                    {isLoading && "⏳"}
                    {!isLoading && isApplied && "✅"}
                  </StatusIndicator>
                </Flex>
              </SnapshotItem>
            );
          })}
        </SnapshotList>
      </Flex>

      {/* Editor view */}
      {selectedSnapshotId && (
        <Flex direction="column" gap="0" style={{ flex: 1 }}>
          <Flex direction="row" justify="between" align="center">
            <p style={{ fontSize: "11px" }}>
              Note Content
              {selectedSnapshotResult.data?.data && (
                <>
                  {" "}
                  - v{selectedSnapshotResult.data.data.note_content_document_version} -{" "}
                  {DateTime.fromISO(
                    selectedSnapshotResult.data.data.note_content_document_locally_modified_at
                  ).toFormat("MMM d, yyyy h:mm a")}
                </>
              )}
            </p>
            <DangerButton onClick={handleReplaceNote} disabled={!selectedSnapshotResult.data}>
              <AlertTriangle size={12} style={{ marginRight: "4px" }} />
              Replace Current Note
            </DangerButton>
          </Flex>
          <NoteContentEditorWrapper>
            <MemCommonEditor
              editorInstanceRef={editorInstanceRef}
              editorInitializer={editorInitializer}
              editorEventHandler={editorEventHandler}
            />
          </NoteContentEditorWrapper>
        </Flex>
      )}
    </Flex>
  );
};

const NoteContentEditorWrapper = styled.div`
  * {
    font-family:
      -apple-system, "system-ui", "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
  }
  font-weight: normal;
  border: 1px solid var(--gray-8);
  padding: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
  max-width: calc(100% - 16px);
  border-radius: 4px;
`;

const SnapshotList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 600px;
  overflow-y: auto;
  padding: 4px;
  border: 1px solid var(--gray-8);
  border-radius: 4px;
  font-size: 12px;
`;

const SnapshotItem = styled.div<{ selected?: boolean }>`
  height: 26px;
  min-height: 26px;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  background-color: ${({ selected }) => (selected ? "var(--gray-3)" : "transparent")};
  &:hover {
    background-color: ${({ selected }) => (selected ? "var(--gray-3)" : "var(--gray-2)")};
  }
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StatusIndicator = styled.div`
  font-size: 14px;
  margin-left: auto;
`;

const DangerButton = styled.button`
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #dc2626;
  background-color: transparent;
  color: #dc2626;
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover {
    background-color: #dc26261a;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: transparent;
  }
`;
